import React, { useEffect } from 'react';
import { BladeModal } from '@monavate/ui-core';
import styled from 'styled-components';
import { Flex } from 'antd';
import { closeLinkPaymentModal } from '@/reduxStore/slices/linkPaymentSlice';
import { useSelector } from 'react-redux';
import { RootState, useAppDispatch, useAppSelector } from '@/reduxStore/rootReducer';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { linkPaymentSchema } from '@/helpers/schemaValidations';
import { Select } from '@monavate/ui-core';
import { Asterisk } from '@monavate/ui-core';
import { Label } from '@monavate/ui-core';
import { fetchOrdersList } from '@/reduxStore/slices/ordersSlice';
import { Select as AntSelect } from 'antd';
import { paymentApi } from '@/services/rest/apiInstances';
import PaymentOption from '@/components/PaymentOption/PaymentOption';
import { Toggle } from '@monavate/ui-core';
import { addNotification } from '@/reduxStore/slices/systemNotificationSlice';
import { getErrorMessageFromResponse } from '@/utils/errorHandling';
import { buildClientApiConfig } from '@/helpers/apiRequestConfig';

const Header = styled.div`
    padding: 32px 32px 24px;
    background: ${(props) => props.theme['Surface/secondary']};
`;

const ToggleContainer = styled.div`
    padding: 12px 4px;
`;

const Option = styled(AntSelect.Option)`
    padding: 0;
`;

const OrderName = styled.div`
    font-size: 13px;
    font-weight: 500;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const OrderOption = styled.div`
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
`;

const MainTitle = styled.div`
    color: ${(props) => props.theme['Content/primary']}
        /* TODO: check color. Alternatives: ["Content/primary","Content/logo","Content/on-accent"] */;
    font-size: 16px;
    font-style: normal;
    font-weight: 600;
    line-height: 24px;
`;

const PaddingContainer = styled.div`
    padding: 24px 0 0 0;
`;

const SecondaryTitle = styled.div`
    color: ${(props) => props.theme['Content/primary']}
        /* TODO: check color. Alternatives: ["Content/primary","Content/logo","Content/on-accent"] */;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 22px;
`;

interface LinkPaymentModalProps {
    isOpen: boolean;
    afterLinkCallback: Function;
}

const BladeModalHeader: React.FC<{ transactionId: string }> = ({ transactionId }) => {
    return (
        <Header>
            <MainTitle>Link to payment instruction</MainTitle>
            <SecondaryTitle>{transactionId}</SecondaryTitle>
        </Header>
    );
};

const LinkPaymentModal: React.FC<LinkPaymentModalProps> = ({ isOpen, afterLinkCallback }) => {
    const dispatch = useAppDispatch();

    const [knowReferenceChecked, setKnowReferenceChecked] = React.useState<boolean>(false);
    const [paymentsList, setPaymentsList] = React.useState<any[]>([]);
    const transactionToDisplay = useSelector(
        (state: RootState) => state.linkPayment.transactionToDisplay,
    );
    const orderList = useAppSelector((state: RootState) => state.orders.orderList);

    const {
        register,
        formState: { errors },
        setValue,
        watch,
    } = useForm({
        resolver: yupResolver(linkPaymentSchema),
        mode: 'all',
    });

    const orderWatch = watch('reference');
    const paymentWatch = watch('paymentInstruction');

    const fetchPaymentInfo = async (reference?: string) => {
        const requestParams = {
            cardId: transactionToDisplay?.cardTransaction?.cardId,
            orderReference: reference,
        };
        if (!reference) {
            delete requestParams.orderReference;
        }

        try {
            const options = buildClientApiConfig();
            const responseData = await paymentApi.listPayments(requestParams, options);
            const allPayment = responseData.data.payments;
            if (allPayment) {
                setPaymentsList(allPayment);
            } else {
                setPaymentsList([]);
            }
        } catch (e: any) {
            console.log(e);
        }
    };

    const onCloseModalHandler = () => {
        dispatch(closeLinkPaymentModal());
    };

    const onSubmitHandler = async () => {
        try {
            const options = buildClientApiConfig();
            await paymentApi.linkTransaction(
                {
                    paymentId: paymentWatch || '',
                    transactionId: transactionToDisplay?.id || '',
                },
                options,
            );

            dispatch(
                addNotification({
                    message: 'Transaction successfully linked to payment instruction',
                    status: 'success',
                }),
            );
            afterLinkCallback();
            onCloseModalHandler();
        } catch (error) {
            dispatch(
                addNotification({
                    message: getErrorMessageFromResponse(error, 'Something went wrong'),
                    status: 'error',
                }),
            );
        }
    };

    useEffect(() => {
        dispatch(fetchOrdersList({}));
        // fetchPaymentInfo();
    }, []);

    useEffect(() => {
        fetchPaymentInfo();
        setValue('reference', null);
        setValue('paymentInstruction', null);
    }, [knowReferenceChecked]);

    const handleChangeOrder = (reference: string) => {
        setValue('reference', reference);
        fetchPaymentInfo(reference);
    };

    const handleChangePayment = (payment: string) => {
        setValue('paymentInstruction', payment);
        const currentOrder = paymentsList?.find((item) => item.id === payment).orderReference;
        setValue('reference', currentOrder);
    };

    return (
        <BladeModal
            isOpen={isOpen}
            closeButton={true}
            handleClose={onCloseModalHandler}
            padding={'0 32px'}
            confirmDisabled={!paymentWatch}
            customHeader={<BladeModalHeader transactionId={transactionToDisplay?.id || ''} />}
            divider
            handleSubmit={onSubmitHandler}
            footerStyle={{ justifyContent: 'flex-end' }}
            confirmButtonText={'Link'}
            cancelButtonText={'Cancel'}
        >
            <PaddingContainer>
                <Flex vertical gap={24}>
                    <div>
                        <Label className="test2">
                            Order reference <Asterisk>*</Asterisk>
                        </Label>
                        <ToggleContainer>
                            <Toggle
                                checked={knowReferenceChecked}
                                toggleText="I know order reference"
                                handleChange={() => {
                                    setKnowReferenceChecked(!knowReferenceChecked);
                                }}
                            />
                        </ToggleContainer>
                        <Select
                            register={() => register('reference')}
                            disabled={!knowReferenceChecked}
                            showSearch
                            error={errors?.reference}
                            value={orderWatch}
                            onSelect={handleChangeOrder}
                            placeholder={'Select order reference'}
                            required
                        >
                            {orderList.map((order: any, index: number) => {
                                return (
                                    <Option
                                        key={index}
                                        value={order.reference}
                                        label={order.reference}
                                    >
                                        <OrderOption>
                                            <OrderName>{order.reference}</OrderName>
                                        </OrderOption>
                                    </Option>
                                );
                            })}
                        </Select>
                    </div>
                    <div>
                        <Select
                            label={'Payment instruction'}
                            register={() => register('paymentInstruction')}
                            disabled={knowReferenceChecked ? !orderWatch : false}
                            showSearch
                            onSelect={handleChangePayment}
                            optionPadding={'0'}
                            listHeight={500}
                            value={paymentWatch}
                            placement={'bottomLeft'}
                            optionLabelProp={'label'}
                            optionFilterProp={'label'}
                            placeholder={
                                knowReferenceChecked
                                    ? 'Select payment instruction ID'
                                    : 'Enter payment instruction ID'
                            }
                            required
                        >
                            {paymentsList?.map((payment: any, index: number) => {
                                return (
                                    <Option value={payment.id} label={payment.id} key={index}>
                                        <PaymentOption payment={payment} />
                                    </Option>
                                );
                            })}
                        </Select>
                    </div>
                </Flex>
            </PaddingContainer>
        </BladeModal>
    );
};

export default LinkPaymentModal;
